import { useCallback, useMemo, useRef } from 'react';
import type { FlagGroup } from '@/models/template';
import { getFlagVariant } from './flagConstants';
import FlagCheckbox from './FlagCheckbox/FlagCheckbox';
import styles from './FlagsBar.module.css';

interface FlagsBarProps {
  flagGroups: FlagGroup[];
  selectedFlags: Set<string>;
  onFlagToggle: (flagClass: string, checked: boolean) => void;
  disabled?: boolean;
  disabledFlags?: Set<string>;
  disabledFlagHints?: Map<string, string>;
}

export default function FlagsBar({
  flagGroups,
  selectedFlags,
  onFlagToggle,
  disabled = false,
  disabledFlags,
  disabledFlagHints,
}: FlagsBarProps) {
  const onFlagToggleRef = useRef(onFlagToggle);
  onFlagToggleRef.current = onFlagToggle;

  const toggleCacheRef = useRef(new Map<string, (checked: boolean) => void>());

  const getToggleHandler = useCallback((flagClass: string) => {
    const cached = toggleCacheRef.current.get(flagClass);
    if (cached) return cached;
    const handler = (checked: boolean) => {
      onFlagToggleRef.current(flagClass, checked);
    };
    toggleCacheRef.current.set(flagClass, handler);
    return handler;
  }, []);

  // Flatten all flags from all groups, defensively excluding follow-up entries
  const flags = useMemo(
    () =>
      flagGroups
        .flatMap((group) => group.flagLabels)
        .filter((f) => !f.flagClass.includes('FOLLOW_UP')),
    [flagGroups]
  );

  return (
    <div className={styles.bar} data-skip-tab>
      {flags.map((flag) => (
        <FlagCheckbox
          key={flag.uuid}
          label={flag.label}
          checked={selectedFlags.has(flag.flagClass)}
          onChange={getToggleHandler(flag.flagClass)}
          variant={getFlagVariant(flag.flagClass)}
          disabled={disabled || disabledFlags?.has(flag.flagClass)}
          title={disabledFlagHints?.get(flag.flagClass)}
        />
      ))}
    </div>
  );
}
